import { isEmpty } from "lodash"
import { PublicPractice } from "../../../domain/Profile/schema"
import { LiabilityInsurance } from "../../../domain/Provider/schema"
import {
  LiabilityStatusEnum,
  getAggregateInsuranceStatus,
} from "../../shared/utils"

export interface LocationLiabilityStatus {
  status: LiabilityStatusEnum
  isGroupPolicy: boolean
  hasActiveLiability: boolean
}

/**
 * Determines the liability insurance status for a specific location
 * based on the practice's group policy or provider's individual policy
 */
export const getLocationLiabilityStatus = (
  practice: PublicPractice,
  providerLiabilityInsurances?: LiabilityInsurance[],
): LocationLiabilityStatus => {
  // Check if practice provides group liability insurance
  const practiceHasGroupPolicy = practice.authReleaseRequired

  if (practiceHasGroupPolicy) {
    // Use practice group liability insurance
    if (isEmpty(practice.liabilityInsurances)) {
      return {
        status: LiabilityStatusEnum.REQUIRED,
        isGroupPolicy: true,
        hasActiveLiability: false,
      }
    }

    const practiceStatus = getAggregateInsuranceStatus(
      (practice.liabilityInsurances || []).map((insurance) => ({
        liabilityInsuranceStatusDisplayId: insurance.insuranceStatus,
      })),
    )

    return {
      status: practiceStatus,
      isGroupPolicy: true,
      hasActiveLiability:
        practiceStatus === LiabilityStatusEnum.ACTIVE ||
        practiceStatus === LiabilityStatusEnum.EXPIRING_SOON,
    }
  } else {
    // Use provider individual liability insurance
    if (isEmpty(providerLiabilityInsurances)) {
      return {
        status: LiabilityStatusEnum.REQUIRED,
        isGroupPolicy: false,
        hasActiveLiability: false,
      }
    }

    const providerStatus = getAggregateInsuranceStatus(
      providerLiabilityInsurances || [],
    )

    return {
      status: providerStatus,
      isGroupPolicy: false,
      hasActiveLiability:
        providerStatus === LiabilityStatusEnum.ACTIVE ||
        providerStatus === LiabilityStatusEnum.EXPIRING_SOON,
    }
  }
}

/**
 * Checks if any practice for the given provider has liability issues.
 * Used for determining if the header banner should be shown.
 */
export const hasLiabilityIssues = (
  practices: PublicPractice[],
  providerLiabilityInsurances?: LiabilityInsurance[],
): boolean => {
  return practices.some((practice) => {
    const status = getLocationLiabilityStatus(
      practice,
      providerLiabilityInsurances,
    )
    return status.status !== LiabilityStatusEnum.ACTIVE
  })
}
